import { db } from "../firebase";
import { ref, onValue, set, get, update, remove } from "firebase/database";
import { detectEntryType, formatBujoLine } from "../utils/bujo";
import { titleFromPaste } from "../utils/normalizePaste";
import { faviconFromText, needsOgUpgrade, resolveLinkThumb } from "../utils/linkThumb";
import { textReaderPath } from "../utils/textRoutes";
import { DEFAULT_CODE_PROJECTS, resolveCodeProjects } from "../config/codeProjects";

const WORKSPACE_REF = "workspace";
const SETTINGS_REF = "workspace/settings";

/** Temas › {categoría} › Notas — avoids Buzón › Capturas › Rápidas */
export const CATEGORY_PATHS = {
  código: { deskName: "Temas", drawerName: "Código", notebookName: "Notas" },
  iglesia: { deskName: "Temas", drawerName: "Iglesia", notebookName: "Notas" },
  vida: { deskName: "Temas", drawerName: "Vida", notebookName: "Notas" },
  cuentos: { deskName: "Temas", drawerName: "Cuentos", notebookName: "Notas" },
};

/** Merge Firebase settings.categoryPaths over defaults (names or desk/drawer/notebook IDs). */
export function resolveCategoryPaths(settings = {}) {
  return { ...CATEGORY_PATHS, ...(settings.categoryPaths || {}) };
}

export async function resolveCategoryPath(category, settings = {}) {
  const merged = resolveCategoryPaths(settings)[category];
  if (!merged) return null;

  await loadCache();
  if (merged.deskId && merged.drawerId && merged.notebookId) {
    const nb = localCache.desks?.[merged.deskId]?.drawers?.[merged.drawerId]?.notebooks?.[merged.notebookId];
    if (nb) return { ...merged, _resolvedBy: "id" };
  }
  return { ...merged, _resolvedBy: "name" };
}

export async function saveCategoryPath(category, path) {
  await set(ref(db, `${SETTINGS_REF}/categoryPaths/${category}`), path);
}

export async function bindCategoryFolder(category, deskId, drawerId, notebookId) {
  await saveCategoryPath(category, { deskId, drawerId, notebookId });
}

let localCache = null;
let lastServerCache = null;
let cacheLoaded = false;
const subscribers = new Set();

function cloneCache(value) {
  if (value == null) return value;
  return JSON.parse(JSON.stringify(value));
}

function isPlainRecord(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

/**
 * Build a Firebase multi-location patch from the last server snapshot to our
 * locally-mutated cache. This is the key durability invariant: never replace
 * the whole workspace with a stale client snapshot.
 */
function buildFirebasePatch(before, after, prefix = "", patch = {}) {
  if (Object.is(before, after)) return patch;

  const beforeIsObject = isPlainRecord(before);
  const afterIsObject = isPlainRecord(after);

  if (!beforeIsObject || !afterIsObject) {
    if (!prefix) return patch;
    const clean = stripUndefined(after);
    patch[prefix] = clean === undefined ? null : clean;
    return patch;
  }

  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  for (const key of keys) {
    const path = prefix ? `${prefix}/${key}` : key;
    const hadBefore = Object.prototype.hasOwnProperty.call(before, key);
    const hasAfter = Object.prototype.hasOwnProperty.call(after, key);

    if (!hasAfter) {
      patch[path] = null;
      continue;
    }

    if (!hadBefore) {
      const clean = stripUndefined(after[key]);
      if (clean !== undefined) patch[path] = clean;
      continue;
    }

    buildFirebasePatch(before[key], after[key], path, patch);
  }

  return patch;
}

async function loadCache() {
  if (cacheLoaded && localCache) return localCache;
  try {
    const snapshot = await get(ref(db, WORKSPACE_REF));
    const data = snapshot.val() || { desks: {} };
    if (!data.desks) data.desks = {};
    localCache = data;
    lastServerCache = cloneCache(data);
    cacheLoaded = true;
    return localCache;
  } catch (e) {
    console.error("[workspaceService] loadCache error:", e);
    localCache = null;
    lastServerCache = null;
    cacheLoaded = false;
    throw new Error("No se pudo cargar la nube. No se escribió nada; tu texto local debe conservarse.");
  }
}

async function saveCache() {
  try {
    if (!localCache || !lastServerCache) {
      throw new Error("No hay una copia de servidor verificada para guardar con seguridad.");
    }

    const payload = stripUndefined(localCache) || { desks: {} };
    const patch = buildFirebasePatch(lastServerCache, payload);

    if (Object.keys(patch).length > 0) {
      await update(ref(db, WORKSPACE_REF), patch);
    }

    // Do not overwrite localCache/lastServerCache here. onValue is the source
    // of truth and may already contain a newer concurrent server snapshot.
    notifySubscribers();
  } catch (e) {
    console.error("[workspaceService] saveCache error:", e);
    const code = e?.code || e?.message || "error desconocido";
    throw new Error(`No se pudo guardar la nota (${code}). Revisá el tamaño de la nota/imagen y las reglas de Firebase.`);
  }
}

function notifySubscribers() {
  subscribers.forEach((cb) => cb(normalizeWorkspace(localCache)));
}

/** Force-refresh workspace cache and notify all subscribers. */
export async function refreshWorkspace() {
  cacheLoaded = false;
  await loadCache();
  notifySubscribers();
  return normalizeWorkspace(localCache);
}

// ─── Subscribe to entire workspace (Real-time) ───
export function subscribeWorkspace(callback) {
  subscribers.add(callback);
  
  const wRef = ref(db, WORKSPACE_REF);
  const unsub = onValue(wRef, (snapshot) => {
    const data = snapshot.val() || { desks: {} };
    if (!data.desks) data.desks = {};
    localCache = data;
    lastServerCache = cloneCache(data);
    cacheLoaded = true;
    callback(normalizeWorkspace(localCache));
  });

  return () => {
    unsub();
    subscribers.delete(callback);
  };
}

// ─── Seed initial workspace ───
export async function seedWorkspace() {
  const data = await loadCache();
  if (data && Object.keys(data.desks || {}).length > 0) {
    await ensureCodeProjects();
    return;
  }

  const seed = buildSeedWorkspace();
  await set(ref(db, WORKSPACE_REF), seed);
  localCache = seed;
  lastServerCache = cloneCache(seed);
  cacheLoaded = true;
  await ensureCodeProjects();
}

function buildSeedWorkspace() {
  const dId = pushKey();
  const drId = pushKey();
  const nId = pushKey();
  return {
    desks: {
      [dId]: {
        name: "Escritorio Principal",
        slug: "escritorio-principal",
        order: 0,
        drawers: {
          [drId]: {
            name: "Cajón de React",
            slug: "cajon-de-react",
            order: 0,
            notebooks: {
              [nId]: {
                name: "Fundamentos",
                slug: "fundamentos",
                color: "#61DAFB",
                order: 0,
                texts: {
                  [pushKey()]: {
                    title: "Hooks básicos",
                    slug: "hooks-basicos",
                    content: "<p>useState y useEffect son los hooks más fundamentales de React.</p>",
                    order: 0,
                    updatedAt: Date.now(),
                  },
                },
              },
            },
          },
        },
      },
    },
  };
}

/** Ensure Temas has the four main category drawers (Código, Iglesia, Vida, Cuentos). */
export async function ensureMainCategories() {
  await loadCache();
  let changed = false;
  const deskName = "Temas";
  let deskId = Object.entries(localCache.desks || {}).find(([, d]) => d.name === deskName)?.[0];
  if (!deskId) {
    deskId = pushKey();
    localCache.desks[deskId] = {
      name: deskName,
      slug: generateSlug(deskName),
      order: -2,
      drawers: {},
    };
    changed = true;
  }
  const desk = localCache.desks[deskId];
  if (!desk.drawers) desk.drawers = {};

  const categories = Object.values(CATEGORY_PATHS);
  categories.forEach((path, i) => {
    let drawerId = Object.entries(desk.drawers).find(([, d]) => d.name === path.drawerName)?.[0];
    if (!drawerId) {
      drawerId = pushKey();
      desk.drawers[drawerId] = {
        name: path.drawerName,
        slug: generateSlug(path.drawerName),
        order: i,
        notebooks: {},
      };
      changed = true;
    }
    const drawer = desk.drawers[drawerId];
    if (!drawer.notebooks) drawer.notebooks = {};
    const nbName = path.notebookName || "Notas";
    const hasNb = Object.values(drawer.notebooks).some((n) => n.name === nbName);
    if (!hasNb) {
      const nId = pushKey();
      drawer.notebooks[nId] = {
        name: nbName,
        slug: generateSlug(nbName),
        color: "#D4AF37",
        order: 0,
        texts: {},
      };
      changed = true;
    }
  });

  if (changed) await saveCache();
}

/** Ensure Temas › Código has portfolio project notebooks (non-destructive). */
export async function ensureCodeProjects() {
  await ensureMainCategories();
  const list = await fetchCodeProjectsFromSettings();
  await ensureCodeProjectsFromList(list);
}

async function fetchCodeProjectsFromSettings() {
  try {
    const snap = await get(ref(db, `${SETTINGS_REF}/codeProjects`));
    return resolveCodeProjects({ codeProjects: snap.val() });
  } catch {
    return resolveCodeProjects({});
  }
}

async function ensureCodeProjectsFromList(projects) {
  await loadCache();
  const paths = CATEGORY_PATHS.código;
  const { deskName, drawerName, notebookName } = paths;

  let deskId = Object.entries(localCache.desks || {}).find(([, d]) => d.name === deskName)?.[0];
  if (!deskId) {
    deskId = pushKey();
    localCache.desks[deskId] = {
      name: deskName,
      slug: generateSlug(deskName),
      order: -2,
      drawers: {},
    };
  }
  const desk = localCache.desks[deskId];

  let drawerId = Object.entries(desk.drawers || {}).find(([, d]) => d.name === drawerName)?.[0];
  if (!drawerId) {
    drawerId = pushKey();
    desk.drawers[drawerId] = {
      name: drawerName,
      slug: generateSlug(drawerName),
      order: 0,
      notebooks: {},
    };
  }
  const drawer = desk.drawers[drawerId];
  if (!drawer.notebooks) drawer.notebooks = {};

  let changed = false;
  const ensureNb = (name, color, order) => {
    const exists = Object.values(drawer.notebooks).some((n) => n.name === name);
    if (exists) return;
    const id = pushKey();
    drawer.notebooks[id] = {
      name,
      slug: generateSlug(name),
      color: color || "#D4AF37",
      order,
      texts: {},
    };
    changed = true;
  };

  ensureNb(notebookName, "#94a3b8", 0);
  (projects || DEFAULT_CODE_PROJECTS).forEach((p, i) => ensureNb(p.notebookName, p.color, i + 1));

  if (changed) await saveCache();
}

export async function saveCodeProjects(projects) {
  const normalized = (projects || []).map((p, i) => ({
    id: p.id,
    label: String(p.label || p.notebookName || p.id).trim(),
    notebookName: String(p.notebookName || p.label || p.id).trim(),
    color: p.color || "#D4AF37",
    order: i,
  }));
  await set(ref(db, `${SETTINGS_REF}/codeProjects`), normalized);
  await ensureCodeProjectsFromList(normalized);
  return normalized;
}

/** Firebase RTDB drops empty objects, so drawers/notebooks/texts may be missing on read. */
function ensureDesk(deskId) {
  if (!localCache.desks) localCache.desks = {};
  const desk = localCache.desks[deskId];
  if (!desk) throw new Error("Escritorio no encontrado");
  if (!desk.drawers) desk.drawers = {};
  return desk;
}

function ensureDrawer(deskId, drawerId) {
  const desk = ensureDesk(deskId);
  const drawer = desk.drawers[drawerId];
  if (!drawer) throw new Error("Cajón no encontrado");
  if (!drawer.notebooks) drawer.notebooks = {};
  return drawer;
}

function ensureNotebook(deskId, drawerId, notebookId) {
  const drawer = ensureDrawer(deskId, drawerId);
  const notebook = drawer.notebooks[notebookId];
  if (!notebook) throw new Error("Cuaderno no encontrado");
  if (!notebook.texts) notebook.texts = {};
  return notebook;
}

function getTextNode(deskId, drawerId, notebookId, textId) {
  try {
    const notebook = ensureNotebook(deskId, drawerId, notebookId);
    return notebook.texts[textId] || null;
  } catch {
    return null;
  }
}

/** Firebase rejects `undefined` anywhere in the payload. */
function stripUndefined(value) {
  if (value === undefined) return undefined;
  if (value === null || typeof value !== "object") return value;
  if (Array.isArray(value)) {
    return value.map(stripUndefined).filter((v) => v !== undefined);
  }
  const out = {};
  for (const [k, v] of Object.entries(value)) {
    if (v === undefined) continue;
    const cleaned = stripUndefined(v);
    if (cleaned !== undefined) out[k] = cleaned;
  }
  return out;
}

function sanitizeGridLayout(layout) {
  if (!Array.isArray(layout)) return [];
  return layout
    .map((item) => stripUndefined(item))
    .filter((item) => item && item.i != null);
}

// ─── CRUD: Desks ───
export async function createDesk(name) {
  await loadCache();
  if (!localCache.desks) localCache.desks = {};
  const key = pushKey();
  const slug = generateSlug(name);
  localCache.desks[key] = { name, slug, order: Date.now(), drawers: {} };
  await saveCache();
  return key;
}

export async function deleteDesk(deskId) {
  await loadCache();
  if (localCache.desks) delete localCache.desks[deskId];
  await saveCache();
}

export async function renameDesk(deskId, name) {
  await loadCache();
  const desk = ensureDesk(deskId);
  desk.name = name;
  desk.slug = generateSlug(name);
  await saveCache();
}

// ─── CRUD: Drawers ───
export async function createDrawer(deskId, name) {
  await loadCache();
  const desk = ensureDesk(deskId);
  const key = pushKey();
  const slug = generateSlug(name);
  desk.drawers[key] = {
    name,
    slug,
    order: Date.now(),
    notebooks: {},
  };
  await saveCache();
  return key;
}

export async function deleteDrawer(deskId, drawerId) {
  await loadCache();
  const desk = ensureDesk(deskId);
  delete desk.drawers[drawerId];
  await saveCache();
}

export async function renameDrawer(deskId, drawerId, name) {
  await loadCache();
  const drawer = ensureDrawer(deskId, drawerId);
  drawer.name = name;
  drawer.slug = generateSlug(name);
  await saveCache();
}

// ─── CRUD: Notebooks ───
export async function createNotebook(deskId, drawerId, name, color) {
  await loadCache();
  const drawer = ensureDrawer(deskId, drawerId);
  const key = pushKey();
  const slug = generateSlug(name);
  drawer.notebooks[key] = {
    name,
    slug,
    color: color || "#8B4513",
    order: Date.now(),
    texts: {},
  };
  await saveCache();
  return key;
}

export async function deleteNotebook(deskId, drawerId, notebookId) {
  await loadCache();
  const drawer = ensureDrawer(deskId, drawerId);
  delete drawer.notebooks[notebookId];
  await saveCache();
}

export async function renameNotebook(deskId, drawerId, notebookId, name) {
  await loadCache();
  const notebook = ensureNotebook(deskId, drawerId, notebookId);
  notebook.name = name;
  notebook.slug = generateSlug(name);
  await saveCache();
}

// ─── Helpers ───
function pushKey() {
  const time = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 8);
  return time + rand;
}

export function generateSlug(title) {
  const s = String(title || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return s || `nota-${Date.now()}`;
}

async function ensureUniqueSlug(slug, existingTexts = []) {
  let uniqueSlug = slug;
  let counter = 1;
  const slugs = new Set(existingTexts.map(t => t.slug).filter(Boolean));
  
  while (slugs.has(uniqueSlug)) {
    uniqueSlug = `${slug}-${counter}`;
    counter++;
  }
  return uniqueSlug;
}

export async function getAllTexts() {
  await loadCache();
  const all = [];
  for (const [dId, desk] of Object.entries(localCache.desks || {})) {
    const deskSlug = desk.slug || generateSlug(desk.name);
    for (const [drId, drawer] of Object.entries(desk.drawers || {})) {
      const drawerSlug = drawer.slug || generateSlug(drawer.name);
      for (const [nId, notebook] of Object.entries(drawer.notebooks || {})) {
        const notebookSlug = notebook.slug || generateSlug(notebook.name);
        for (const [tId, text] of Object.entries(notebook.texts || {})) {
          const dateSlug = text.dateSlug || new Date(text.updatedAt || Date.now()).toISOString().split('T')[0];
          all.push({ 
            id: tId, 
            ...text, 
            dateSlug,
            deskId: dId,
            deskName: desk.name,
            deskSlug, 
            drawerId: drId,
            drawerName: drawer.name,
            drawerSlug, 
            notebookId: nId,
            notebookName: notebook.name,
            notebookSlug,
            notebookColor: notebook.color,
          });
        }
      }
    }
  }
  return all;
}

// ─── CRUD: Texts ───
export async function getNotebookSafe(deskId, drawerId, notebookId) {
  await loadCache();
  const desk = localCache?.desks?.[deskId];
  const drawer = desk?.drawers?.[drawerId];
  const notebook = drawer?.notebooks?.[notebookId];

  if (!desk || !drawer || !notebook) {
    const missing = {
      desk: !desk,
      drawer: !drawer,
      notebook: !notebook,
      deskId,
      drawerId,
      notebookId,
    };
    console.warn("[workspaceService] getNotebookSafe missing", missing);
    throw new Error("No se pudo guardar: no se encontró el destino (escritorio/cajón/cuaderno).");
  }

  return notebook;
}

export async function getTextSafe(deskId, drawerId, notebookId, textId) {
  const notebook = await getNotebookSafe(deskId, drawerId, notebookId);
  const text = notebook?.texts?.[textId];
  if (!text) {
    console.warn("[workspaceService] getTextSafe missing", {
      deskId,
      drawerId,
      notebookId,
      textId,
      hasTexts: !!notebook?.texts,
    });
    throw new Error("No se pudo guardar: la página ya no existe en la nube.");
  }
  return text;
}

export async function createText(
  deskId,
  drawerId,
  notebookId,
  title,
  content,
  img = null,
  options = {}
) {
  await loadCache();
  const allTexts = await getAllTexts();
  const baseSlug = generateSlug(title);
  const slug = await ensureUniqueSlug(baseSlug, allTexts);

  const desk = ensureDesk(deskId);
  const drawer = ensureDrawer(deskId, drawerId);
  const notebook = ensureNotebook(deskId, drawerId, notebookId);

  const key = pushKey();
  const dateSlug = new Date().toISOString().split("T")[0];

  const createdAt = Date.now();
  const body = content ?? "";
  const resolvedImg = img || (await resolveLinkThumb(title, body)) || faviconFromText(title, body);
  const newText = {
    id: key,
    title,
    content: body,
    img: resolvedImg,
    slug,
    dateSlug,
    order: createdAt,
    updatedAt: createdAt,
    deskId,
    deskName: desk.name,
    deskSlug: desk.slug || generateSlug(desk.name),
    drawerId,
    drawerName: drawer.name,
    drawerSlug: drawer.slug || generateSlug(drawer.name),
    notebookId,
    notebookName: notebook.name,
    notebookSlug: notebook.slug || generateSlug(notebook.name),
    notebookColor: notebook.color,
  };

  if (options.kind === "folder" || options.kind === "note") {
    newText.kind = options.kind;
  }
  if (options.parentId) {
    newText.parentId = options.parentId;
  }
  if (typeof options.done === "boolean") {
    newText.done = options.done;
  } else if (options.parentId) {
    newText.done = false;
  }

  // Puntual write: evitamos pisar el workspace completo.
  const textRef = ref(db, `workspace/desks/${deskId}/drawers/${drawerId}/notebooks/${notebookId}/texts/${key}`);
  await set(textRef, newText);

  // Actualizamos cache local en memoria para que la UI responda inmediato.
  notebook.texts[key] = newText;
  notifySubscribers();
  return key;
}

/** Pure helpers — folder-note checklist (children = rows). */
export function getChildren(texts, parentId) {
  if (!parentId) return [];
  return (texts || [])
    .filter((t) => t.parentId === parentId)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
}

export function getTopLevelTexts(texts) {
  return (texts || [])
    .filter((t) => !t.parentId)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
}

export function folderProgress(children) {
  const list = children || [];
  const total = list.length;
  const done = list.filter((t) => !!t.done).length;
  return { done, total, ratio: total ? done / total : 0 };
}

export function isFolderNote(text) {
  return text?.kind === "folder";
}

function wouldCreateParentCycle(textsById, textId, newParentId) {
  if (!newParentId) return false;
  if (newParentId === textId) return true;
  let cur = textsById[newParentId];
  const seen = new Set();
  while (cur?.parentId) {
    if (cur.parentId === textId) return true;
    if (seen.has(cur.parentId)) break;
    seen.add(cur.parentId);
    cur = textsById[cur.parentId];
  }
  return false;
}

/** Create a Scrivener-style folder note (project). */
export async function createFolderNote(deskId, drawerId, notebookId, title, content = "") {
  return createText(deskId, drawerId, notebookId, title, content || "", null, {
    kind: "folder",
  });
}

/**
 * Convert note ↔ folder. Folder→note promotes children to this note's parent
 * so they don't disappear from the tree (kids only render under kind:folder).
 */
export async function setTextKind(deskId, drawerId, notebookId, textId, kind) {
  if (kind !== "folder" && kind !== "note") {
    throw new Error(`kind inválido: ${kind}`);
  }
  await loadCache();
  const notebook = localCache.desks[deskId]?.drawers?.[drawerId]?.notebooks?.[notebookId];
  const text = notebook?.texts?.[textId];
  if (!text) throw new Error("Nota no encontrada");

  const prev = text.kind || "note";
  text.kind = kind;
  text.updatedAt = Date.now();

  if (prev === "folder" && kind === "note") {
    const promoteTo = text.parentId || null;
    for (const child of Object.values(notebook.texts || {})) {
      if (child.parentId !== textId) continue;
      if (promoteTo) child.parentId = promoteTo;
      else delete child.parentId;
      child.updatedAt = Date.now();
    }
  }

  await saveCache();
  return text;
}

/** Ensure Proyectos › Activos › Proyectos notebook exists; create a folder-note there. */
export async function createProjectFolder(title, content = "") {
  const folder = await ensureFolderByNames("Proyectos", "Activos", "Proyectos", {
    color: "#D4AF37",
  });
  await saveCache();
  const id = await createFolderNote(
    folder.deskId,
    folder.drawerId,
    folder.notebookId,
    title,
    content
  );
  return { id, ...folder };
}

/** Add a checklist row = child note under a folder. */
export async function addChecklistItem(
  deskId,
  drawerId,
  notebookId,
  parentId,
  title,
  { asFolder = false } = {}
) {
  return createText(deskId, drawerId, notebookId, title, "", null, {
    parentId,
    kind: asFolder ? "folder" : "note",
    done: false,
  });
}

export async function setTextDone(deskId, drawerId, notebookId, textId, done) {
  await loadCache();
  const text = getTextNode(deskId, drawerId, notebookId, textId);
  if (!text) return;
  text.done = !!done;
  text.updatedAt = Date.now();
  await saveCache();
}

export async function toggleTextDone(deskId, drawerId, notebookId, textId) {
  await loadCache();
  const text = getTextNode(deskId, drawerId, notebookId, textId);
  if (!text) return;
  text.done = !text.done;
  text.updatedAt = Date.now();
  await saveCache();
  return text.done;
}

/**
 * Reparent a text under another text in the same notebook (or clear parent).
 * Cycle-guard: cannot parent under own descendant.
 */
export async function moveTextUnderParent(
  deskId,
  drawerId,
  notebookId,
  textId,
  parentId
) {
  await loadCache();
  const notebook = localCache.desks[deskId]?.drawers?.[drawerId]?.notebooks?.[notebookId];
  const text = notebook?.texts?.[textId];
  if (!text) throw new Error("Text not found");

  const nextParent = parentId || null;
  if (nextParent) {
    const parent = notebook.texts[nextParent];
    if (!parent) throw new Error("Parent not found in notebook");
    if (wouldCreateParentCycle(notebook.texts, textId, nextParent)) {
      throw new Error("Cannot nest a note under its own descendant");
    }
  }

  if (nextParent) {
    text.parentId = nextParent;
    if (typeof text.done !== "boolean") text.done = false;
  } else {
    delete text.parentId;
  }
  text.updatedAt = Date.now();
  await saveCache();
}

/** All top-level folder notes across the workspace (projects). */
export async function getProjectFolders() {
  const all = await getAllTexts();
  return all
    .filter((t) => t.kind === "folder" && !t.parentId)
    .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
}

/**
 * One-shot: Deepgram rich-payload reference note (Ayuda › Referencia › APIs).
 * Idempotent via settings.deepgramGuideNote.
 */
export async function ensureDeepgramGuideNote() {
  const settingKey = "deepgramGuideNote";
  try {
    const snap = await get(ref(db, `${SETTINGS_REF}/${settingKey}`));
    if (snap.exists() && snap.val()) return snap.val();
  } catch {
    /* continue */
  }

  const { buildDeepgramGuideMarkdown, DEEPGRAM_GUIDE_TITLE } = await import(
    "../config/deepgramGuide"
  );

  const saved = await createArticle(DEEPGRAM_GUIDE_TITLE, buildDeepgramGuideMarkdown(), null, {
    deskName: "Ayuda",
    drawerName: "Referencia",
    notebookName: "APIs",
    color: "#61DAFB",
    tags: ["deepgram", "voz", "api", "referencia"],
    isPinned: true,
  });

  const meta = {
    textId: saved.id,
    deskId: saved.deskId,
    drawerId: saved.drawerId,
    notebookId: saved.notebookId,
    createdAt: Date.now(),
  };
  await set(ref(db, `${SETTINGS_REF}/${settingKey}`), meta);
  return meta;
}

/**
 * Crea (una vez por versión) una nota legible en Ayuda › Novedades
 * con el resumen de la actualización y cómo usar la feature.
 */
export async function ensureWhatsNewGuideNote(version) {
  if (!version) return null;
  const settingKey = `whatsNewGuide:${version}`;
  try {
    const snap = await get(ref(db, `${SETTINGS_REF}/${settingKey}`));
    if (snap.exists() && snap.val()) return snap.val();
  } catch {
    /* continue and try create */
  }

  const { getReleaseNotes } = await import("../config/releaseNotes");
  const notes = getReleaseNotes(version);
  if (!notes) return null;

  const title = `Novedades v${version} — ${notes.title}`;
  const content = [
    `# ${notes.title}`,
    "",
    `**Versión ${version}**`,
    "",
    notes.summary,
    "",
    "## Qué se hizo",
    "",
    ...notes.highlights.map((h) => `- ${h}`),
    "",
    "## Cómo usarlo",
    "",
    ...notes.howTo.map((h, i) => `${i + 1}. ${h}`),
    "",
    "---",
    "",
    "Esta nota se creó al actualizar la app. Podés buscarla por «Novedades» en el buscador o en Ayuda › Novedades.",
  ].join("\n");

  const saved = await createArticle(title, content, null, {
    deskName: "Ayuda",
    drawerName: "Novedades",
    notebookName: "Actualizaciones",
    color: "#D4AF37",
    tags: ["novedades", "guia", `v${version}`],
    isPinned: true,
  });

  const meta = {
    textId: saved.id,
    deskId: saved.deskId,
    drawerId: saved.drawerId,
    notebookId: saved.notebookId,
    version,
    createdAt: Date.now(),
  };
  await set(ref(db, `${SETTINGS_REF}/${settingKey}`), meta);
  return meta;
}

export async function updateText(
  deskId,
  drawerId,
  notebookId,
  textId,
  updates
) {
  try {
    await loadCache();
    const text = await getTextSafe(deskId, drawerId, notebookId, textId);

    const nextUpdates = { ...(updates || {}) };
    const updatedAt = Date.now();

    if (nextUpdates.title && nextUpdates.title !== text.title) {
      const allTexts = await getAllTexts();
      const baseSlug = generateSlug(nextUpdates.title);
      nextUpdates.slug = await ensureUniqueSlug(baseSlug, allTexts.filter((t) => t.id !== textId));
    }

    // Rapid Logging: Extract tags from content automatically (#tag)
    if (nextUpdates.content) {
      const autoTags = (nextUpdates.content.match(/#(\w+)/g) || []).map((t) => t.slice(1));
      const currentTags = nextUpdates.tags || text.tags || [];
      nextUpdates.tags = [...new Set([...currentTags, ...autoTags])];
    }

    // Link paste → article og:image (fallback favicon) when missing or still a favicon
    const nextTitle = nextUpdates.title ?? text.title;
    const nextContent = nextUpdates.content ?? text.content;
    const nextImg = nextUpdates.img !== undefined ? nextUpdates.img : text.img;
    if (needsOgUpgrade(nextImg, nextTitle, nextContent)) {
      const thumb = await resolveLinkThumb(nextTitle, nextContent);
      if (thumb) nextUpdates.img = thumb;
    }

    const firebaseUpdates = { ...nextUpdates, updatedAt };

    // Cache local en memoria
    Object.assign(text, firebaseUpdates);
    notifySubscribers();

    // Puntual write: update directo al nodo del texto.
    const textRef = ref(db, `workspace/desks/${deskId}/drawers/${drawerId}/notebooks/${notebookId}/texts/${textId}`);
    await update(textRef, firebaseUpdates);

    // A green save receipt means Firebase can immediately read the same prose back.
    const verifiedSnapshot = await get(textRef);
    const verified = verifiedSnapshot.val();
    const titleMatches = nextUpdates.title === undefined || verified?.title === firebaseUpdates.title;
    const contentMatches = nextUpdates.content === undefined || verified?.content === firebaseUpdates.content;
    if (!verifiedSnapshot.exists() || !titleMatches || !contentMatches) {
      throw new Error("Firebase respondió al guardado, pero no pudimos verificar el texto. El borrador debe conservarse.");
    }
  } catch (err) {
    const path = `workspace/desks/${deskId}/drawers/${drawerId}/notebooks/${notebookId}/texts/${textId}`;
    console.error("[workspaceService] save text failed", {
      path,
      deskId,
      drawerId,
      notebookId,
      textId,
      error: err?.message || String(err),
    });
    throw err;
  }
}

export async function toggleReadStatus(deskId, drawerId, notebookId, textId) {
  await loadCache();
  const text = getTextNode(deskId, drawerId, notebookId, textId);
  if (text) {
    text.isRead = !text.isRead;
    text.readAt = text.isRead ? Date.now() : null;
    text.updatedAt = Date.now();
    await saveCache();
  }
}

export async function setReadingMeta(deskId, drawerId, notebookId, textId, patch) {
  await loadCache();
  const text = getTextNode(deskId, drawerId, notebookId, textId);
  if (!text) return null;
  Object.assign(text, patch, { updatedAt: Date.now() });
  await saveCache();
  return text;
}

/** Swipe / inbox actions: later, read, review, archive, trash, restore */
export async function applyReadingAction(deskId, drawerId, notebookId, textId, action) {
  const { READING, nextReviewAt } = await import("../config/readingStates");
  await loadCache();
  const text = getTextNode(deskId, drawerId, notebookId, textId);
  if (!text) throw new Error("Nota no encontrada");

  const patch = {};
  switch (action) {
    case "read":
      patch.isRead = true;
      patch.readAt = Date.now();
      if (!text.readingState || text.readingState === READING.LATER) {
        patch.readingState = READING.INBOX;
      }
      break;
    case "unread":
      patch.isRead = false;
      patch.readAt = null;
      break;
    case "later":
      patch.readingState = READING.LATER;
      break;
    case "review": {
      const count = (text.reviewCount || 0) + 1;
      patch.readingState = READING.REVIEW;
      patch.reviewCount = count;
      patch.reviewAt = nextReviewAt(count - 1);
      patch.isRead = true;
      patch.readAt = Date.now();
      break;
    }
    case "archive":
      patch.readingState = READING.ARCHIVED;
      patch.isRead = true;
      patch.readAt = text.readAt || Date.now();
      break;
    case "trash":
      patch.readingState = READING.TRASH;
      break;
    case "restore":
      patch.readingState = READING.INBOX;
      break;
    default:
      throw new Error(`Acción de lectura desconocida: ${action}`);
  }

  Object.assign(text, patch, { updatedAt: Date.now() });
  await saveCache();
  return text;
}

export async function togglePin(deskId, drawerId, notebookId, textId) {
  await loadCache();
  const text = getTextNode(deskId, drawerId, notebookId, textId);
  if (text) {
    text.isPinned = !text.isPinned;
    text.updatedAt = Date.now();
    await saveCache();
  }
}

export async function updateTextColor(deskId, drawerId, notebookId, textId, color) {
  await loadCache();
  const text = getTextNode(deskId, drawerId, notebookId, textId);
  if (text) {
    text.color = color;
    text.updatedAt = Date.now();
    await saveCache();
  }
}

export async function toggleTaskLine(deskId, drawerId, notebookId, textId, lineIndex) {
  await loadCache();
  const text = getTextNode(deskId, drawerId, notebookId, textId);
  if (!text || typeof text.content !== 'string') return;

  const lines = text.content.split('\n');
  if (lineIndex < 0 || lineIndex >= lines.length) return;

  const line = lines[lineIndex];
  if (/^(\s*)•\s/.test(line)) {
    lines[lineIndex] = line.replace(/^(\s*)•\s/, '$1[x] ');
  } else if (/^(\s*)\[x\]/i.test(line)) {
    lines[lineIndex] = line.replace(/^(\s*)\[x\]\s*/i, '$1• ');
  } else if (line.includes('[ ]')) {
    lines[lineIndex] = line.replace('[ ]', '[x]');
  } else if (/(\[x\]|\[X\])/.test(line)) {
    lines[lineIndex] = line.replace(/\[x\]/i, '[ ]');
  } else {
    return;
  }

  text.content = lines.join('\n');
  text.updatedAt = Date.now();
  await saveCache();
}

export async function reorderLineInText(deskId, drawerId, notebookId, textId, fromIndex, toIndex) {
  if (fromIndex === toIndex) return;
  await loadCache();
  const text = localCache.desks[deskId]?.drawers?.[drawerId]?.notebooks?.[notebookId]?.texts?.[textId];
  if (!text?.content) return;
  const lines = text.content.split('\n');
  if (fromIndex < 0 || fromIndex >= lines.length || toIndex < 0 || toIndex >= lines.length) return;
  const [line] = lines.splice(fromIndex, 1);
  lines.splice(toIndex, 0, line);
  text.content = lines.join('\n');
  text.updatedAt = Date.now();
  await saveCache();
}

export async function migrateDiarioLine(deskId, drawerId, notebookId, textId, lineIndex) {
  await loadCache();
  const text = localCache.desks[deskId]?.drawers?.[drawerId]?.notebooks?.[notebookId]?.texts?.[textId];
  if (!text?.content) return;
  const lines = text.content.split('\n');
  if (lineIndex < 0 || lineIndex >= lines.length) return;
  const entry = detectEntryType(lines[lineIndex].trim());
  if (!entry || entry.type === 'migrated') return;
  lines[lineIndex] = formatBujoLine('migrated', entry.text);
  text.content = lines.join('\n');
  text.updatedAt = Date.now();
  await saveCache();
}

export async function deleteText(deskId, drawerId, notebookId, textId) {
  console.log("[deleteText] start", { deskId, drawerId, notebookId, textId });
  await loadCache();

  const texts =
    localCache?.desks?.[deskId]?.drawers?.[drawerId]?.notebooks?.[notebookId]?.texts;
  const existed = Boolean(texts && Object.prototype.hasOwnProperty.call(texts, textId));
  const fbPath = `${WORKSPACE_REF}/desks/${deskId}/drawers/${drawerId}/notebooks/${notebookId}/texts/${textId}`;

  console.log("[deleteText] pre-check", {
    fbPath,
    existed,
    siblingIds: texts ? Object.keys(texts) : null,
  });

  if (!existed) {
    console.warn("[deleteText] not found in cache — aborting", { deskId, drawerId, notebookId, textId });
    throw new Error("Nota no encontrada en el cuaderno indicado");
  }

  // ponytail: remove() at leaf path — full saveCache() raced with onValue and resurrected deleted notes
  try {
    await remove(ref(db, fbPath));
    console.log("[deleteText] firebase remove ok", { fbPath, textId });
  } catch (e) {
    console.error("[deleteText] firebase remove failed", { fbPath, textId, error: e });
    throw e;
  }

  delete localCache.desks[deskId].drawers[drawerId].notebooks[notebookId].texts[textId];
  notifySubscribers();
  console.log("[deleteText] done", { textId });
}

// ─── Create article (simplified) ───
export async function createArticle(title, content, img = null, options = {}) {
  await loadCache();
  const allTexts = await getAllTexts();
  const baseSlug = generateSlug(title);
  const slug = await ensureUniqueSlug(baseSlug, allTexts);

  let deskId = options.deskId;
  let drawerId = options.drawerId;
  let notebookId = options.notebookId;
  let createdDesk = false;
  let createdDrawer = false;
  let createdNotebook = false;

  if (deskId && drawerId && notebookId) {
    const nb = localCache.desks?.[deskId]?.drawers?.[drawerId]?.notebooks?.[notebookId];
    if (!nb) throw new Error("Carpeta de categoría no encontrada");
  } else {
    const deskName = options.deskName || "Buzón de Entrada";
    const drawerName = options.drawerName || "Capturas";
    const notebookName = options.notebookName || "Rápidas";

    deskId = Object.entries(localCache.desks || {}).find(([, d]) => d.name === deskName)?.[0];
    if (!deskId) {
      deskId = pushKey();
      localCache.desks[deskId] = { name: deskName, slug: generateSlug(deskName), order: -1, drawers: {} };
      createdDesk = true;
    }
    const desk = localCache.desks[deskId];
    if (!desk.drawers) desk.drawers = {};
    drawerId = Object.entries(desk.drawers || {}).find(([, d]) => d.name === drawerName)?.[0];
    if (!drawerId) {
      drawerId = pushKey();
      desk.drawers[drawerId] = { name: drawerName, slug: generateSlug(drawerName), order: 0, notebooks: {} };
      createdDrawer = true;
    }
    const drawer = desk.drawers[drawerId];
    if (!drawer.notebooks) drawer.notebooks = {};
    notebookId = Object.entries(drawer.notebooks || {}).find(([, n]) => n.name === notebookName)?.[0];
    if (!notebookId) {
      notebookId = pushKey();
      drawer.notebooks[notebookId] = {
        name: notebookName,
        slug: generateSlug(notebookName),
        color: options.color || "#D4AF37",
        order: 0,
        texts: {},
      };
      createdNotebook = true;
    }
  }

  const desk = localCache.desks[deskId];
  const drawer = desk.drawers[drawerId];
  const notebook = drawer.notebooks[notebookId];

  const key = pushKey();
  const dateSlug = new Date().toISOString().split('T')[0];
  
  // Extract tags from content automatically (#tag)
  const autoTags = (content.match(/#(\w+)/g) || []).map(t => t.slice(1));
  const tags = [...new Set([...(options.tags || []), ...autoTags])];

  const resolvedImg = img || (await resolveLinkThumb(title, content)) || faviconFromText(title, content);
  const createdAt = Date.now();
  const text = { 
    id: key,
    title, 
    content, 
    img: resolvedImg, 
    slug, 
    dateSlug,
    tags,
    order: createdAt, 
    updatedAt: createdAt,
    isRead: false,
    isPinned: !!options.isPinned,
    color: options.color || null,
    deskId,
    deskName: desk.name,
    deskSlug: desk.slug,
    drawerId,
    drawerName: drawer.name,
    drawerSlug: drawer.slug,
    notebookId,
    notebookName: notebook.name,
    notebookSlug: notebook.slug,
    notebookColor: notebook.color
  };

  // Atomic, path-level write. Never serialize the entire workspace for a new article.
  const writes = {};
  if (createdDesk) {
    writes[`desks/${deskId}/name`] = desk.name;
    writes[`desks/${deskId}/slug`] = desk.slug;
    writes[`desks/${deskId}/order`] = desk.order;
  }
  if (createdDrawer) {
    writes[`desks/${deskId}/drawers/${drawerId}/name`] = drawer.name;
    writes[`desks/${deskId}/drawers/${drawerId}/slug`] = drawer.slug;
    writes[`desks/${deskId}/drawers/${drawerId}/order`] = drawer.order;
  }
  if (createdNotebook) {
    writes[`desks/${deskId}/drawers/${drawerId}/notebooks/${notebookId}/name`] = notebook.name;
    writes[`desks/${deskId}/drawers/${drawerId}/notebooks/${notebookId}/slug`] = notebook.slug;
    writes[`desks/${deskId}/drawers/${drawerId}/notebooks/${notebookId}/color`] = notebook.color;
    writes[`desks/${deskId}/drawers/${drawerId}/notebooks/${notebookId}/order`] = notebook.order;
  }

  const textPath = `desks/${deskId}/drawers/${drawerId}/notebooks/${notebookId}/texts/${key}`;
  writes[textPath] = stripUndefined(text);
  await update(ref(db, WORKSPACE_REF), writes);

  // Do not show "Guardado" until the exact title/body can be read back.
  const textRef = ref(db, `${WORKSPACE_REF}/${textPath}`);
  const verifiedSnapshot = await get(textRef);
  const verified = verifiedSnapshot.val();
  if (!verifiedSnapshot.exists() || verified?.title !== title || verified?.content !== content) {
    throw new Error("Firebase respondió al guardado, pero no pudimos verificar el artículo. El borrador debe conservarse.");
  }

  const currentNotebook = localCache?.desks?.[deskId]?.drawers?.[drawerId]?.notebooks?.[notebookId];
  if (currentNotebook) {
    if (!currentNotebook.texts) currentNotebook.texts = {};
    currentNotebook.texts[key] = { ...text, ...verified };
    notifySubscribers();
  }

  return { ...text, ...verified };
}


// ─── Helpers ───
function normalizeWorkspace(data) {
  const desks = toSortedArray(data.desks);
  desks.forEach(d => {
    if (!d.slug) d.slug = generateSlug(d.name);
    const drawers = toSortedArray(d.drawers);
    drawers.forEach(dr => {
      if (!dr.slug) dr.slug = generateSlug(dr.name);
      const notebooks = toSortedArray(dr.notebooks);
      notebooks.forEach(n => {
        if (!n.slug) n.slug = generateSlug(n.name);
      });
    });
  });
  return { desks };
}

/** Longform manuscript tools. */
export async function exportManuscriptMarkdown({ deskId, drawerId, notebookId } = {}) {
  const texts = await getAllTexts();
  const selected = texts.filter((t) => (!deskId || t.deskId === deskId) && (!drawerId || t.drawerId === drawerId) && (!notebookId || t.notebookId === notebookId));
  const byParent = new Map();
  selected.forEach((t) => { const key = t.parentId || ""; byParent.set(key, [...(byParent.get(key) || []), t]); });
  const render = (parentId = "", depth = 0) => (byParent.get(parentId) || []).sort((a,b) => (a.order || 0) - (b.order || 0)).map((t) => {
    const heading = "#".repeat(Math.min(6, depth + 1));
    const body = String(t.content || "").replace(/<img[^>]*src=["']data:[^"']+["'][^>]*>/gi, "[imagen incrustada]");
    return `${heading} ${t.title || "Sin título"}\n\n${body}\n\n${render(t.id, depth + 1)}`;
  }).join("\n");
  return render();
}

export async function saveManuscriptSnapshot(label = "Snapshot") {
  await loadCache();
  const key = pushKey();
  const snapshot = { label: String(label).slice(0, 120), createdAt: Date.now(), workspace: stripUndefined(localCache) };
  await set(ref(db, `${SETTINGS_REF}/manuscriptSnapshots/${key}`), snapshot);
  return { id: key, ...snapshot };
}

export async function listManuscriptSnapshots() {
  const snapshot = await get(ref(db, `${SETTINGS_REF}/manuscriptSnapshots`));
  return Object.entries(snapshot.val() || {}).map(([id, value]) => ({ id, ...value })).sort((a,b) => (b.createdAt || 0) - (a.createdAt || 0));
}

export async function replaceInManuscript(search, replacement = "", options = {}) {
  const needle = String(search || "");
  if (!needle) throw new Error("Indicá qué texto buscar.");
  const texts = await getAllTexts();
  const targets = texts.filter((t) => (!options.notebookId || t.notebookId === options.notebookId) && String(t.content || "").includes(needle));
  for (const text of targets) {
    const content = String(text.content || "").split(needle).join(String(replacement));
    await updateText(text.deskId, text.drawerId, text.notebookId, text.id, { content });
  }
  return { count: targets.length };
}

export async function splitTextAt(deskId, drawerId, notebookId, textId, offset) {
  await loadCache();
  const text = await getTextSafe(deskId, drawerId, notebookId, textId);
  const body = String(text.content || "");
  const point = Math.max(1, Math.min(body.length - 1, Number(offset) || 0));
  const left = body.slice(0, point).trim();
  const right = body.slice(point).trim();
  await updateText(deskId, drawerId, notebookId, textId, { content: left });
  const created = await createText(deskId, drawerId, notebookId, `${text.title} (continuación)`, right, null, { kind: text.kind || "note", parentId: text.parentId || null });
  return created;
}

export async function mergeTexts(deskId, drawerId, notebookId, firstId, secondId) {
  await loadCache();
  const first = await getTextSafe(deskId, drawerId, notebookId, firstId);
  const second = await getTextSafe(deskId, drawerId, notebookId, secondId);
  await updateText(deskId, drawerId, notebookId, firstId, { content: `${first.content || ""}\n\n${second.content || ""}`.trim() });
  await deleteText(deskId, drawerId, notebookId, secondId);
  return getTextSafe(deskId, drawerId, notebookId, firstId);
}
export function toSortedArray(obj) {
  if (!obj) return [];
  return Object.entries(obj)
    .map(([id, value]) => ({ id, ...value }))
    .sort((a, b) => (a.order || 0) - (b.order || 0));
}

export async function findTextBySlugAndDate(slug, date) {
  return findTextBySlugAndDateAttempt(slug, date, false);
}

async function findTextBySlugAndDateAttempt(slug, date, forceRefresh) {
  if (forceRefresh) { cacheLoaded = false; localCache = null; }
  await loadCache();
  for (const [dId, desk] of Object.entries(localCache.desks || {})) {
    for (const [drId, drawer] of Object.entries(desk.drawers || {})) {
      for (const [nId, notebook] of Object.entries(drawer.notebooks || {})) {
        const entries = Object.entries(notebook.texts || {});
        let match = entries.find(([, t]) => t.slug === slug && t.dateSlug === date);
        if (!match) match = entries.find(([, t]) => t.slug === slug);
        if (match) return { id: match[0], ...match[1], deskId: dId, deskSlug: desk.slug || generateSlug(desk.name), drawerId: drId, drawerSlug: drawer.slug || generateSlug(drawer.name), notebookId: nId, notebookSlug: notebook.slug || generateSlug(notebook.name) };
      }
    }
  }
  if (!forceRefresh) return findTextBySlugAndDateAttempt(slug, date, true);
  return null;
}

export async function findTextById(textId) {
  await loadCache();
  if (!localCache || !localCache.desks) {
    console.error("[workspaceService] findTextById: localCache is empty or invalid");
    return null;
  }

  for (const [dId, desk] of Object.entries(localCache.desks || {})) {
    const deskSlug = desk.slug || generateSlug(desk.name);
    for (const [drId, drawer] of Object.entries(desk.drawers || {})) {
      const drawerSlug = drawer.slug || generateSlug(drawer.name);
      for (const [nId, notebook] of Object.entries(drawer.notebooks || {})) {
        const notebookSlug = notebook.slug || generateSlug(notebook.name);
        const entries = Object.entries(notebook.texts || {});
        
        // Match by ID, Slug, or dateSlug/slug combination
        const match = entries.find(([id, t]) => {
          if (id === textId || t.slug === textId) return true;
          // If textId looks like a slug and we have date param (handled in ArticleView)
          return false; 
        });
        
        // Better: let's add a findTextBySlugAndDate
        
        if (match) {
          return { 
            id: match[0], 
            ...match[1], 
            deskId: dId, 
            deskSlug,
            drawerId: drId, 
            drawerSlug,
            notebookId: nId,
            notebookSlug
          };
        }
      }
    }
  }
  if (cacheLoaded) {
    cacheLoaded = false;
    localCache = null;
    return findTextById(textId);
  }
  console.warn(`[workspaceService] findTextById: No article found for "${textId}"`);
  return null;
}

export async function moveNotebook(srcDeskId, srcDrawerId, notebookId, destDeskId, destDrawerId, destIndex) {
  await loadCache();
  const srcArr = toSortedArray(localCache.desks[srcDeskId].drawers[srcDrawerId].notebooks || {});
  const destArr = toSortedArray(localCache.desks[destDeskId].drawers[destDrawerId].notebooks || {});
  const movedIdx = srcArr.findIndex((n) => n.id === notebookId);
  if (movedIdx === -1) throw new Error("Notebook not found");
  const [moved] = srcArr.splice(movedIdx, 1);
  destArr.splice(destIndex, 0, moved);
  srcArr.forEach((n, i) => { localCache.desks[srcDeskId].drawers[srcDrawerId].notebooks[n.id].order = i; });
  destArr.forEach((n, i) => { localCache.desks[destDeskId].drawers[destDrawerId].notebooks[n.id].order = i; });
  const item = localCache.desks[srcDeskId].drawers[srcDrawerId].notebooks[notebookId];
  localCache.desks[destDeskId].drawers[destDrawerId].notebooks[notebookId] = item;
  delete localCache.desks[srcDeskId].drawers[srcDrawerId].notebooks[notebookId];
  await saveCache();
}

/** Move several notebooks to another drawer in one save. */
export async function moveNotebooksBatch(srcDeskId, srcDrawerId, notebookIds, destDeskId, destDrawerId) {
  if (!notebookIds?.length) return;
  await loadCache();
  const srcNotebooks = localCache.desks[srcDeskId].drawers[srcDrawerId].notebooks || {};
  const destNotebooks = localCache.desks[destDeskId].drawers[destDrawerId].notebooks || {};
  const destArr = toSortedArray(destNotebooks);
  let insertAt = destArr.length;

  for (const notebookId of notebookIds) {
    const srcArr = toSortedArray(srcNotebooks);
    const movedIdx = srcArr.findIndex((n) => n.id === notebookId);
    if (movedIdx === -1) continue;
    srcArr.splice(movedIdx, 1);
    for (let i = 0; i < srcArr.length; i += 1) {
      const id = srcArr[i].id;
      localCache.desks[srcDeskId].drawers[srcDrawerId].notebooks[id].order = i;
    }
    const item = localCache.desks[srcDeskId].drawers[srcDrawerId].notebooks[notebookId];
    if (!item) continue;
    localCache.desks[destDeskId].drawers[destDrawerId].notebooks[notebookId] = item;
    delete localCache.desks[srcDeskId].drawers[srcDrawerId].notebooks[notebookId];
    destArr.splice(insertAt, 0, { ...item, id: notebookId });
    insertAt += 1;
  }

  for (let i = 0; i < destArr.length; i += 1) {
    const id = destArr[i].id;
    localCache.desks[destDeskId].drawers[destDrawerId].notebooks[id].order = i;
  }
  await saveCache();
}

export async function reorderDesks(deskIds) {
  await loadCache();
  deskIds.forEach((id, index) => { if (localCache.desks[id]) localCache.desks[id].order = index; });
  await saveCache();
}

export async function reorderNotebooks(deskId, drawerId, notebookIds) {
  await loadCache();
  notebookIds.forEach((id, index) => {
    const nb = localCache.desks[deskId]?.drawers?.[drawerId]?.notebooks?.[id];
    if (nb) nb.order = index;
  });
  await saveCache();
}

export async function moveText(srcDeskId, srcDrawerId, srcNotebookId, textId, destDeskId, destDrawerId, destNotebookId) {
  await loadCache();
  const text = localCache.desks[srcDeskId].drawers[srcDrawerId].notebooks[srcNotebookId].texts[textId];
  if (!text) throw new Error("Text not found");

  const desk = localCache.desks[destDeskId];
  const drawer = desk.drawers[destDrawerId];
  const notebook = drawer.notebooks[destNotebookId];

  if (!notebook.texts) notebook.texts = {};

  // Ensure the moved item lands at the end immediately (reorderTexts will refine ordering).
  try {
    const destArr = toSortedArray(notebook.texts);
    text.order = destArr.length;
  } catch (_e) {
    // ignore; reorderTexts is called by the UI right after move.
  }

  text.deskId = destDeskId;
  text.deskName = desk.name;
  text.deskSlug = desk.slug;
  text.drawerId = destDrawerId;
  text.drawerName = drawer.name;
  text.drawerSlug = drawer.slug;
  text.notebookId = destNotebookId;
  text.notebookName = notebook.name;
  text.notebookSlug = notebook.slug;
  text.notebookColor = notebook.color;
  // Parent links are notebook-local; clear when leaving the notebook.
  if (
    srcDeskId !== destDeskId ||
    srcDrawerId !== destDrawerId ||
    srcNotebookId !== destNotebookId
  ) {
    delete text.parentId;
  }
  text.updatedAt = Date.now();
  notebook.texts[textId] = text;

  delete localCache.desks[srcDeskId].drawers[srcDrawerId].notebooks[srcNotebookId].texts[textId];

  await saveCache();
}

/** Move several texts to another notebook in one save. */
export async function moveTextsBatch(srcDeskId, srcDrawerId, srcNotebookId, textIds, destDeskId, destDrawerId, destNotebookId) {
  if (!textIds?.length) return;
  await loadCache();
  const destTexts = localCache.desks[destDeskId].drawers[destDrawerId].notebooks[destNotebookId].texts || {};
  const destArr = toSortedArray(destTexts);
  let order = destArr.length;
  for (const textId of textIds) {
    const text = localCache.desks[srcDeskId]?.drawers?.[srcDrawerId]?.notebooks?.[srcNotebookId]?.texts?.[textId];
    if (!text) continue;
    text.order = order++;
    destTexts[textId] = text;
    delete localCache.desks[srcDeskId].drawers[srcDrawerId].notebooks[srcNotebookId].texts[textId];
  }
  localCache.desks[destDeskId].drawers[destDrawerId].notebooks[destNotebookId].texts = destTexts;
  await saveCache();
}

export async function reorderTexts(deskId, drawerId, notebookId, textIds) {
  await loadCache();
  textIds.forEach((id, index) => {
    const t = localCache.desks[deskId]?.drawers?.[drawerId]?.notebooks?.[notebookId]?.texts?.[id];
    if (t) t.order = index;
  });
  await saveCache();
}

/** Find or create desk › drawer › notebook by display names. */
async function ensureFolderByNames(deskName, drawerName, notebookName, { color } = {}) {
  await loadCache();
  let deskId = Object.entries(localCache.desks || {}).find(([, d]) => d.name === deskName)?.[0];
  if (!deskId) {
    deskId = pushKey();
    localCache.desks[deskId] = { name: deskName, slug: generateSlug(deskName), order: -1, drawers: {} };
  }
  const desk = localCache.desks[deskId];
  let drawerId = Object.entries(desk.drawers || {}).find(([, d]) => d.name === drawerName)?.[0];
  if (!drawerId) {
    drawerId = pushKey();
    desk.drawers[drawerId] = { name: drawerName, slug: generateSlug(drawerName), order: 0, notebooks: {} };
  }
  const drawer = desk.drawers[drawerId];
  let notebookId = Object.entries(drawer.notebooks || {}).find(([, n]) => n.name === notebookName)?.[0];
  if (!notebookId) {
    notebookId = pushKey();
    drawer.notebooks[notebookId] = {
      name: notebookName,
      slug: generateSlug(notebookName),
      color: color || "#D4AF37",
      order: Date.now(),
      texts: {},
    };
  }
  return {
    deskId,
    drawerId,
    notebookId,
    deskName: desk.name,
    drawerName: drawer.name,
    notebookName: drawer.notebooks[notebookId].name,
  };
}

/** Resolve Temas › categoría › cuaderno folder (creates if missing). */
export async function ensureCategoryFolder(category, settings = {}, notebookNameOverride = null) {
  const paths = await resolveCategoryPath(category, settings);
  if (!paths) throw new Error("Categoría inválida");
  const notebookName = notebookNameOverride || paths.notebookName;
  const project = resolveCodeProjects(settings).find((p) => p.notebookName === notebookName);

  await loadCache();
  if (paths._resolvedBy === "id" && paths.deskId && paths.drawerId) {
    const desk = localCache.desks[paths.deskId];
    const drawer = desk?.drawers?.[paths.drawerId];
    if (!desk || !drawer) {
      return ensureFolderByNames(paths.deskName, paths.drawerName, notebookName, { color: project?.color });
    }
    if (!notebookNameOverride && paths.notebookId && drawer.notebooks?.[paths.notebookId]) {
      const nb = drawer.notebooks[paths.notebookId];
      return {
        deskId: paths.deskId,
        drawerId: paths.drawerId,
        notebookId: paths.notebookId,
        deskName: desk.name,
        drawerName: drawer.name,
        notebookName: nb.name,
      };
    }
    let notebookId = Object.entries(drawer.notebooks || {}).find(([, n]) => n.name === notebookName)?.[0];
    if (!notebookId) {
      notebookId = pushKey();
      drawer.notebooks[notebookId] = {
        name: notebookName,
        slug: generateSlug(notebookName),
        color: project?.color || "#D4AF37",
        order: Date.now(),
        texts: {},
      };
      await saveCache();
    }
    return {
      deskId: paths.deskId,
      drawerId: paths.drawerId,
      notebookId,
      deskName: desk.name,
      drawerName: drawer.name,
      notebookName: drawer.notebooks[notebookId].name,
    };
  }

  const folder = await ensureFolderByNames(paths.deskName, paths.drawerName, notebookName, { color: project?.color });
  await saveCache();
  return folder;
}

/** Move a standalone note to a category folder (one tap from save receipt). */
export async function moveTextToFolder(
  { deskId, drawerId, notebookId, textId, page },
  category,
  { settings, notebookName } = {}
) {
  const dest = await ensureCategoryFolder(category, settings, notebookName);
  const same =
    deskId === dest.deskId && drawerId === dest.drawerId && notebookId === dest.notebookId;
  if (!same) {
    await moveText(deskId, drawerId, notebookId, textId, dest.deskId, dest.drawerId, dest.notebookId);
  }
  const desk = localCache.desks[dest.deskId];
  const drawer = desk.drawers[dest.drawerId];
  const nb = drawer.notebooks[dest.notebookId];
  const text = nb.texts[textId];
  return {
    unchanged: same,
    receipt: buildSaveReceipt(dest.deskId, dest.drawerId, dest.notebookId, textId, {
      page: page || text?.title,
      section: category.charAt(0).toUpperCase() + category.slice(1),
      findHow: `Cajones → ${dest.deskName} → ${dest.drawerName}`,
      preview: text?.title,
      canQuickMove: true,
    }),
  };
}

// ─── Daily Log Helpers ───
async function syncDailyLogPath(path) {
  try {
    await set(ref(db, `${SETTINGS_REF}/dailyLogPath`), path);
  } catch (e) {
    console.warn("[workspaceService] syncDailyLogPath failed:", e);
  }
}

async function getOrCreateDailyLog() {
  await loadCache();
  const settingsRef = localCache.settings || {};
  if (settingsRef.dailyLogPath) {
    const { deskId, drawerId, notebookId } = settingsRef.dailyLogPath;
    const nb = localCache.desks?.[deskId]?.drawers?.[drawerId]?.notebooks?.[notebookId];
    if (nb) {
      if (!nb.texts) {
        nb.texts = {};
        await saveCache();
      }
      await syncDailyLogPath({ deskId, drawerId, notebookId });
      return { deskId, drawerId, notebookId };
    }
  }
  const deskId = pushKey();
  const drawerId = pushKey();
  const notebookId = pushKey();
  localCache.desks[deskId] = {
    name: "Diario",
    slug: generateSlug("Diario"),
    order: -9999,
    drawers: {
      [drawerId]: {
        name: "Hoy",
        slug: generateSlug("Hoy"),
        order: 0,
        notebooks: {
          [notebookId]: {
            name: "Registro",
            slug: generateSlug("Registro"),
            color: "#0ea5e9",
            order: 0,
            texts: {},
          },
        },
      },
    },
  };
  localCache.settings = {
    ...localCache.settings,
    dailyLogPath: { deskId, drawerId, notebookId },
  };
  await saveCache();
  await syncDailyLogPath({ deskId, drawerId, notebookId });
  return { deskId, drawerId, notebookId };
}

/** Save to user-configured category folder (settings.categoryPaths) or defaults. */
export async function captureToCategory(category, text, { asTask = false, bujoLine = null, settings = {}, notebookName = null, color = null } = {}) {
  const paths = await resolveCategoryPath(category, settings);
  if (!paths) throw new Error("Categoría inválida");

  const trimmed = (text || "").trim();
  if (!trimmed) return null;

  let content = trimmed;
  if (bujoLine) content = bujoLine;
  else if (asTask) content = `- [ ] ${trimmed}`;

  const title = asTask
    ? trimmed.split("\n")[0].slice(0, 80)
    : titleFromPaste(trimmed);
  const saved = await createArticle(title, content, null, {
    ...paths,
    notebookName: notebookName || paths.notebookName,
    color: color || undefined,
    tags: [category, notebookName].filter(Boolean),
  });

  const receipt = {
    path: `${saved.deskName} › ${saved.drawerName} › ${saved.notebookName}`,
    page: saved.title,
    title: saved.title,
    content: saved.content || trimmed,
    section: category.charAt(0).toUpperCase() + category.slice(1),
    findHow: `Cajones → ${saved.deskName} → ${saved.drawerName}`,
    preview: trimmed.split("\n")[0],
    url: textReaderPath(saved),
    dateSlug: saved.dateSlug,
    textId: saved.id,
    deskId: saved.deskId,
    drawerId: saved.drawerId,
    notebookId: saved.notebookId,
    deskSlug: saved.deskSlug,
    drawerSlug: saved.drawerSlug,
    notebookSlug: saved.notebookSlug,
    slug: saved.slug,
    canQuickMove: true,
    needsPolish: shouldAutoPolish(trimmed, { asTask, bujoLine }),
  };
  return receipt;
}

export function buildSaveReceipt(deskId, drawerId, notebookId, textId, meta = {}) {
  const desk = localCache.desks?.[deskId];
  const drawer = desk?.drawers?.[drawerId];
  const notebook = drawer?.notebooks?.[notebookId];
  const text = notebook?.texts?.[textId] || {};
  const deskSlug = desk?.slug || generateSlug(desk?.name || "escritorio");
  const drawerSlug = drawer?.slug || generateSlug(drawer?.name || "cajon");
  const notebookSlug = notebook?.slug || generateSlug(notebook?.name || "cuaderno");
  const enriched = {
    ...text,
    id: textId,
    deskId,
    drawerId,
    notebookId,
    deskSlug,
    drawerSlug,
    notebookSlug,
    slug: text.slug || textId,
    dateSlug: text.dateSlug || new Date().toISOString().split("T")[0],
  };
  return {
    path: `${desk?.name || "Diario"} › ${drawer?.name || "Hoy"} › ${notebook?.name || "Registro"}`,
    page: meta.page || text.title || "",
    title: meta.page || text.title || "",
    content: text.content || meta.content || "",
    section: meta.section || "Hoy",
    findHow: meta.findHow || "En esta pantalla, más abajo",
    preview: meta.preview || (text.content || "").split("\n")[0]?.slice(0, 80) || "",
    url: textReaderPath(enriched),
    textId,
    deskId,
    drawerId,
    notebookId,
    deskSlug,
    drawerSlug,
    notebookSlug,
    slug: enriched.slug,
    dateSlug: enriched.dateSlug,
    canQuickMove: meta.canQuickMove !== false,
  };
}

export async function quickCapture(content) {
  const { deskId, drawerId, notebookId } = await getOrCreateDailyLog();
  const title = new Date().toLocaleTimeString("es-AR", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return createText(deskId, drawerId, notebookId, title, content);
}

export async function quickAddTask(taskText) {
  const { deskId, drawerId, notebookId } = await getOrCreateDailyLog();
  await loadCache();
  const title = taskText;
  const content = `- [ ] ${taskText}`;
  const id = await createText(deskId, drawerId, notebookId, title, content);
  return buildSaveReceipt(deskId, drawerId, notebookId, id, {
    page: title,
    section: "Tareas",
    findHow: "Lista «Tareas» en Hoy — podés reordenar",
    preview: taskText,
    canQuickMove: true,
  });
}

/** One rolling note per day — ideas stay together and are easy to reread. */
export async function appendDailySpark(ideaText) {
  const trimmed = (ideaText || "").trim();
  if (!trimmed) return null;

  const { deskId, drawerId, notebookId } = await getOrCreateDailyLog();
  await loadCache();

  const todayKey = new Date().toISOString().split("T")[0];
  const sparkTitle = `Chispas · ${todayKey}`;
  const time = new Date().toLocaleTimeString("es-AR", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const line = `• ${time} — ${trimmed}\n`;

  const texts = localCache.desks[deskId].drawers[drawerId].notebooks[notebookId].texts || {};
  const existing = Object.entries(texts).find(([, t]) => t.title === sparkTitle);

  if (existing) {
    const [sparkId, t] = existing;
    const content = (t.content || "") + line;
    await updateText(deskId, drawerId, notebookId, sparkId, { content });
    return buildSaveReceipt(deskId, drawerId, notebookId, sparkId, {
      page: sparkTitle,
      section: "Ideas de hoy",
      findHow: "Lista «Ideas de hoy» en esta pantalla",
      preview: trimmed,
      canQuickMove: false,
    });
  }

  const id = await createText(deskId, drawerId, notebookId, sparkTitle, line);
  return buildSaveReceipt(deskId, drawerId, notebookId, id, {
    page: sparkTitle,
    section: "Ideas de hoy",
    findHow: "Lista «Ideas de hoy» en esta pantalla",
    preview: trimmed,
    canQuickMove: false,
  });
}

/** BuJo daily page — one notebook spread per day. */
export async function appendDailyLogEntry(input, defaultType = "note") {
  const trimmed = (input || "").trim();
  if (!trimmed) return null;

  const { deskId, drawerId, notebookId } = await getOrCreateDailyLog();
  await loadCache();

  const todayKey = new Date().toISOString().split("T")[0];
  const diarioTitle = `Diario · ${todayKey}`;
  let entry = detectEntryType(trimmed);
  if (!entry) {
    entry = { type: defaultType, text: trimmed, done: false };
  }
  const line = `${formatBujoLine(entry.type, entry.text, entry.done)}\n`;

  const texts = localCache.desks[deskId].drawers[drawerId].notebooks[notebookId].texts || {};
  const existing = Object.entries(texts).find(([, t]) => t.title === diarioTitle);

  if (existing) {
    const [id, t] = existing;
    const content = (t.content || "") + line;
    await updateText(deskId, drawerId, notebookId, id, { content });
    return buildSaveReceipt(deskId, drawerId, notebookId, id, {
      page: diarioTitle,
      section: sectionLabelForType(entry.type),
      findHow: "Sección correspondiente en Hoy, o «Abrir página del diario»",
      preview: entry.text,
      canQuickMove: false,
    });
  }

  const id = await createText(deskId, drawerId, notebookId, diarioTitle, line);
  return buildSaveReceipt(deskId, drawerId, notebookId, id, {
    page: diarioTitle,
    section: sectionLabelForType(entry.type),
    findHow: "En Hoy, debajo del cuadro de captura",
    preview: entry.text,
    canQuickMove: false,
  });
}

function sectionLabelForType(type) {
  const map = {
    task: "Tareas del diario",
    event: "Eventos",
    focus: "Prioridad",
    note: "Notas del diario",
    migrated: "Migrado",
    scheduled: "Programado",
  };
  return map[type] || "Diario de hoy";
}

export async function getDailyLogPath() {
  await loadCache();
  return localCache.settings?.dailyLogPath || null;
}

// ─── Settings (Cloud-synced preferences) ───
const settingsSubscribers = new Set();

export function subscribeSettings(callback) {
  settingsSubscribers.add(callback);
  const sRef = ref(db, SETTINGS_REF);
  const unsub = onValue(sRef, (snapshot) => {
    const data = snapshot.val() || {};
    if (localCache) localCache.settings = data;
    if (lastServerCache) lastServerCache.settings = cloneCache(data);
    callback(data);
  });
  return () => {
    unsub();
    settingsSubscribers.delete(callback);
  };
}

export async function saveCorkboardLayout(layout) {
  await set(ref(db, `${SETTINGS_REF}/corkboardLayout`), sanitizeGridLayout(layout));
}

export async function saveFreeformLayout(layout) {
  await set(ref(db, `${SETTINGS_REF}/freeformLayout`), sanitizeGridLayout(layout));
}

/** Per-folder/notebook corkboard positions: settings.corkboardScopes[scopeKey] = layout[] */
export async function saveCorkboardScopeLayout(scopeKey, layout) {
  if (!scopeKey || typeof scopeKey !== "string") return;
  const clean = sanitizeGridLayout(layout);
  await set(ref(db, `${SETTINGS_REF}/corkboardScopes/${scopeKey}`), clean);
}

/** Soft kanban overlay: { columns: [{id,label}], lists: { [colId]: textId[] } } */
export async function saveKanbanBoard(board) {
  const columns = Array.isArray(board?.columns)
    ? board.columns
        .filter((c) => c && typeof c.id === "string")
        .map((c) => ({
          id: String(c.id),
          label: String(c.label || "Columna").slice(0, 40),
        }))
    : [];
  const lists = {};
  if (board?.lists && typeof board.lists === "object") {
    for (const [colId, arr] of Object.entries(board.lists)) {
      lists[colId] = Array.isArray(arr) ? arr.map(String) : [];
    }
  }
  await set(ref(db, `${SETTINGS_REF}/kanbanBoard`), { columns, lists });
}

export async function saveDashboardLayout(layout) {
  await set(ref(db, `${SETTINGS_REF}/dashboardLayout`), sanitizeGridLayout(layout));
}

export async function savePaneWidth(width) {
  await set(ref(db, `${SETTINGS_REF}/paneWidth`), width);
}

export async function saveExpandedDrawers(map) {
  await set(ref(db, `${SETTINGS_REF}/expandedDrawers`), map);
}

export async function saveDefaultView(view) {
  await set(ref(db, `${SETTINGS_REF}/defaultView`), view);
}

export async function saveCubeNavigationEnabled(enabled) {
  await set(ref(db, `${SETTINGS_REF}/cubeNavigationEnabled`), !!enabled);
}

export async function saveVoiceCaptureStyle(style) {
  await set(ref(db, `${SETTINGS_REF}/voiceCaptureStyle`), style);
}

export async function getDailyFocus() {
  const snapshot = await get(ref(db, `${SETTINGS_REF}/dailyFocus`));
  const focus = snapshot.val() || [];
  const dateSnapshot = await get(ref(db, `${SETTINGS_REF}/dailyFocusDate`));
  const savedDate = dateSnapshot.val();
  const today = new Date().toISOString().split('T')[0];

  if (savedDate && savedDate !== today) {
    // New day: Keep unfinished items, roll them over
    const rolledOver = Array.isArray(focus) 
      ? focus.filter(f => f.text && f.text.trim() && !f.done).map(f => ({ ...f, done: false }))
      : [];
    
    // Fill with empty slots up to at least 3
    const finalFocus = [...rolledOver];
    while (finalFocus.length < 3) {
      finalFocus.push({ text: "", done: false });
    }

    await set(ref(db, `${SETTINGS_REF}/dailyFocusDate`), today);
    await set(ref(db, `${SETTINGS_REF}/dailyFocus`), finalFocus);
    return finalFocus;
  }
  
  if (!savedDate) {
    await set(ref(db, `${SETTINGS_REF}/dailyFocusDate`), today);
  }

  return Array.isArray(focus) ? focus : [];
}

export async function saveDailyFocus(items) {
  await set(ref(db, `${SETTINGS_REF}/dailyFocus`), items);
  const today = new Date().toISOString().split('T')[0];
  await set(ref(db, `${SETTINGS_REF}/dailyFocusDate`), today);
}

// ─── AI Magic ───
export async function callGeminiAPI({ text, title, mode }) {
  const res = await fetch("/api/gemini", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text, title, mode }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.hint || data.error || "Gemini API failed");
    err.quota = Boolean(data.quota);
    throw err;
  }
  return data;
}

/** Free polish: grammar + paragraphs + 3–4 word title + Art Deco thumb. */
export async function enhanceNote({ text, title } = {}) {
  const res = await fetch("/api/note-enhance", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text, title }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "note-enhance failed");
  return data;
}

const POLISH_MIN_CHARS = 40;

/**
 * After save: keep contentRaw, patch polished fields. Never blocks save.
 * Leaves isRead untouched (stays in reading backlog).
 */
export async function polishNoteAfterSave({
  deskId,
  drawerId,
  notebookId,
  textId,
  content,
  title,
} = {}) {
  const raw = String(content || "").trim();
  if (!deskId || !drawerId || !notebookId || !textId) return null;
  if (raw.length < POLISH_MIN_CHARS) return null;

  try {
    const data = await enhanceNote({ text: raw, title });
    const updates = {
      contentRaw: raw,
      title: data.title || title,
      content: data.correctedText || raw,
    };
    if (data.imageDataUrl && data.imageDataUrl.length <= 8000) {
      updates.img = data.imageDataUrl;
    }
    await loadCache();
    const node = getTextNode(deskId, drawerId, notebookId, textId);
    const nextTags = [...new Set([...(node?.tags || []), "auto-polish"])];
    await updateText(deskId, drawerId, notebookId, textId, {
      ...updates,
      tags: nextTags,
    });
    return {
      ...data,
      deskId,
      drawerId,
      notebookId,
      textId,
      title: updates.title,
      content: updates.content,
      img: updates.img || null,
    };
  } catch (err) {
    console.warn("[polishNoteAfterSave]", err?.message || err);
    return null;
  }
}

export function shouldAutoPolish(content, { asTask = false, bujoLine = null } = {}) {
  if (asTask || bujoLine) return false;
  return String(content || "").trim().length >= POLISH_MIN_CHARS;
}

export async function generateAIContent(prompt) {
  const res = await fetch("/api/magic-generate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ prompt }),
  });
  if (!res.ok) throw new Error("AI generation failed");
  return await res.json();
}